import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { type LinkProps, Link } from 'react-router-dom';

type ButtonVariant =
    | 'primary'
    | 'secondary'
    | 'success'
    | 'danger'
    | 'warning'
    | 'info'
    | 'light'
    | 'dark'
    | 'link'
    | 'outline-dark'
    | 'destructive';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: ButtonVariant;
    size?: ButtonSize;
    isLoading?: boolean;
    fullWidth?: boolean;
    icon?: ReactNode;
    children?: ReactNode;
    className?: string;
    disabled?: boolean;
    to?: string; // Optional path for Link component
    linkProps?: Omit<LinkProps, 'to' | 'className'>; // Optional additional Link props
}

const Button = ({
    children,
    variant = 'primary',
    size = 'md',
    isLoading = false,
    fullWidth = false,
    icon,
    className = '',
    disabled,
    to,
    linkProps,
    type = 'button',
    ...rest
}: ButtonProps) => {
    // Tailwind classes based on variant and size
    const getVariantClasses = (variant: ButtonVariant): string => {
        switch (variant) {
            case 'primary':
                return 'bg-p314-blue hover:bg-p314-blue-hover text-white shadow-sm';
            case 'secondary':
                return 'bg-zinc-600 hover:bg-zinc-700 text-white';
            case 'success':
                return 'bg-[#204B3C] hover:bg-[#2A6555] text-white';
            case 'danger':
                return 'bg-red-600 hover:bg-red-700 text-white';
            case 'warning':
                return 'bg-yellow-600 hover:bg-yellow-700 text-white';
            case 'info':
                return 'bg-cyan-600 hover:bg-cyan-700 text-white';
            case 'light':
                return 'bg-zinc-200 hover:bg-zinc-300 text-zinc-800';
            case 'dark':
                return 'bg-zinc-700 hover:bg-zinc-600 text-white rounded transition-colors';
            case 'outline-dark':
                return 'bg-[#1a1a1a] hover:bg-[#2c2c2c] text-white border border-zinc-600 transition-colors';
            case 'destructive':
                return 'bg-[#891F1F] hover:bg-[#A62929] text-white rounded transition-colors';
            case 'link':
                return 'bg-transparent hover:bg-transparent text-blue-600 hover:text-blue-800 hover:underline p-0';
            default:
                return 'bg-blue-600 hover:bg-blue-700 text-white';
        }
    };

    const getSizeClasses = (size: ButtonSize): string => {
        switch (size) {
            case 'sm':
                return 'py-1 px-2 text-sm';
            case 'lg':
                return 'py-2.5 px-5 text-lg';
            case 'md':
            default:
                return 'py-2 px-4 text-base';
        }
    };

    const variantClasses = getVariantClasses(variant);
    const sizeClasses = getSizeClasses(size);
    const widthClass = fullWidth ? 'w-full' : '';
    const disabledClasses = disabled || isLoading ? 'opacity-60 cursor-not-allowed' : '';

    const baseClasses =
        variant !== 'link'
            ? 'inline-flex items-center justify-center font-medium rounded focus:outline-none focus:ring-1 focus:ring-inset focus:ring-opacity-20 transition-all duration-200'
            : '';

    const focusRingColor =
        variant === 'primary'
            ? 'focus:ring-blue-400'
            : variant === 'danger' || variant === 'destructive'
              ? 'focus:ring-red-400'
              : variant === 'success'
                ? 'focus:ring-green-400'
                : variant === 'outline-dark'
                  ? 'focus:ring-zinc-500'
                  : 'focus:ring-zinc-500';

    const combinedClassName = `${baseClasses} ${variantClasses} ${sizeClasses} ${widthClass} ${focusRingColor} ${disabledClasses} ${className}`;

    // Content to render inside the button or link
    const content = (
        <>
            {isLoading && (
                <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline-block"
                    fill="none"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path
                        className="opacity-75"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        fill="currentColor"
                    />
                </svg>
            )}
            {icon && !isLoading && <span className="mr-2">{icon}</span>}
            {children}
        </>
    );

    // If 'to' prop is provided, render a Link component
    if (to) {
        return (
            <Link className={combinedClassName} style={{ pointerEvents: disabled ? 'none' : 'auto' }} to={to} {...linkProps}>
                {content}
            </Link>
        );
    }

    // Otherwise, render a regular button
    return (
        <button className={combinedClassName} disabled={disabled || isLoading} type={type} {...rest}>
            {content}
        </button>
    );
};

export default Button;
